import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import TaskIcon from './TaskIcon';

// Modal shell for the composer, share and schedule dialogs. Full-screen
// sheet on phones. Like Popover, it stops mousedown so record dialogs it may
// be opened from don't treat clicks inside it as "outside".
interface TaskDialogProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}

const TaskDialog: React.FC<TaskDialogProps> = ({ title, onClose, children, footer, wide }) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const overlay = overlayRef.current;
    const stop = (e: MouseEvent) => e.stopPropagation();
    overlay?.addEventListener('mousedown', stop);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      overlay?.removeEventListener('mousedown', stop);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={overlayRef}
      className="tk-dialog-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`tk-dialog ${wide ? 'tk-dialog-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="tk-dialog-head">
          <h3>{title}</h3>
          <button type="button" className="tk-icon-btn" onClick={onClose} aria-label="Close">
            <TaskIcon name="close" />
          </button>
        </header>
        <div className="tk-dialog-body">{children}</div>
        {footer && <footer className="tk-dialog-foot">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
};

export default TaskDialog;
